import React, { useState, useEffect, useRef } from 'react';
import { Tab } from '@headlessui/react';
import StarRating from '../components/StarRating';
import AboutTab from '../components/tabs/AboutTab';
import OfficesTab from '../components/tabs/OfficesTab';
import ReviewsTab from '../components/tabs/ReviewsTab';
import QATab from '../components/tabs/QATab';
import ContactTab from '../components/tabs/ContactTab';
import { useParams } from 'react-router-dom';
import { fetchOrganisation, fetchReviews } from '../services/organisationService';
import OrganisationLogo from '../components/OrganisationLogo';
import SEO from '../components/SEO';
import axios from 'axios';
import { addUtmParams } from '../utils/urlUtils';
import { useAuth } from '../contexts/AuthContext';

// Helper function to generate proper SEO title for organisation profiles
function generateOrgSeoTitle(organisation) {
  if (!organisation) {
    return 'Legal Service Provider';
  }
  
  const firmName = organisation.practice_name;
  // Get location from first office
  const location = organisation.offices?.[0]?.town;
  
  if (firmName && location) {
    // Format: "Shakespeare Martineau | Birmingham"
    return `${firmName} | ${location}`;
  } else if (firmName) {
    // If no location, just use firm name
    return firmName;
  } else {
    return 'Legal Service Provider';
  }
}

const OrganisationPage = () => {
  const { slug } = useParams();
  const { user } = useAuth();
  const [organisation, setOrganisation] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const reviewsTabRef = useRef(null);
  const [seoData, setSeoData] = useState({
    title: '',
    description: '',
    image: ''
  });

  useEffect(() => {
    let mounted = true;
    const loadInitialData = async () => {
      try {
        const orgData = await fetchOrganisation(slug);
        if (!mounted) return;

        if (orgData && orgData.id) {
          const reviewsData = await fetchReviews(orgData.id);
          if (!mounted) return;
          setReviews(reviewsData);
        }

        setOrganisation({
          ...orgData,
          practice_areas: Array.isArray(orgData.practice_areas)
            ? orgData.practice_areas
            : JSON.parse(orgData.practice_areas || '[]')
        });
        setError(null);
      } catch (error) {
        if (!mounted) return;
        console.error('Error fetching data:', error);
        setError('Failed to load organisation data');
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadInitialData();
    return () => { mounted = false; };
  }, [slug]);

  useEffect(() => {
    if (!reviewsTabRef.current || !organisation?.id) return;

    const observer = new IntersectionObserver(
      async (entries) => {
        if (entries[0].isIntersecting) {
          try {
            const reviewsData = await fetchReviews(organisation.id);
            setReviews(reviewsData);
          } catch (error) {
            console.error('Error loading reviews:', error);
          }
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(reviewsTabRef.current);
    return () => observer.disconnect();
  }, [organisation?.id]);

  const handleReviewAdded = async () => {
    try {
      const reviewsData = await fetchReviews(organisation.id, true); // Force refresh
      setReviews(reviewsData);
    } catch (error) {
      console.error('Error refreshing reviews:', error);
    }
  };

  const getAverageRating = () => {
    if (!reviews || !reviews.reviews) return 0;
    
    // If we have stats from the API with average_rating, use that
    if (reviews.stats && reviews.stats.average_rating) {
      return reviews.stats.average_rating;
    }
    
    // Otherwise calculate manually
    const reviewsArray = reviews.reviews || [];
    if (reviewsArray.length === 0) return 0;
    
    const sum = reviewsArray.reduce((acc, review) => acc + review.rating, 0);
    return (sum / reviewsArray.length).toFixed(1);
  };

  useEffect(() => {
    const fetchSeoData = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/seo?url=${window.location.pathname}`
        );
        if (response.data.length > 0) {
          setSeoData(response.data[0]);
        }
      } catch (error) {
        console.error('Error fetching SEO data:', error);
      }
    };

    fetchSeoData();
  }, []);

  if (loading) {
    return (
      <div className="organisation-page__loading">
        <div className="organisation-page__loading-spinner" />
      </div>
    );
  }

  if (!organisation) {
    return (
      <div className="organisation-page">
        <div className="organisation-page__container">
          <h2 className="organisation-page__not-found-title">Organisation not found</h2>
          <p className="organisation-page__not-found-message">
            The organisation you're looking for doesn't exist or has been removed.
          </p>
        </div>
      </div>
    );
  }

  const tabs = [
    { name: 'About', content: <AboutTab organisation={organisation} /> },
    { name: 'Offices', content: <OfficesTab offices={organisation.offices} currentUser={user} /> },
    { name: 'Reviews', content: (
      <div ref={reviewsTabRef}>
        <ReviewsTab 
          organisation={organisation}
          reviews={reviews}
          onReviewAdded={handleReviewAdded}
        />
      </div>
    )},
    { name: 'Q&A', content: <QATab organisation={organisation} /> },
    { name: 'Contact', content: <ContactTab organisation={organisation} currentUser={user} /> }
  ];

  return (
    <>
      <SEO 
        fallbackTitle={generateOrgSeoTitle(organisation)}
        fallbackDescription={organisation ? `Learn about ${organisation.practice_name}, their services, office locations, and contact information. Read reviews and get in touch.` : 'View details about this legal service provider, including services, locations, and contact information.'}
      />
      <div className="organisation-page">

        <div className="organisation-page__hero">
          <h1>{organisation.practice_name}</h1>
          <div className="organisation-page__hero-rating">
            <StarRating rating={Number(getAverageRating())} />
            <span className="organisation-page__hero-rating-count">
              ({reviews && reviews.stats ? reviews.stats.total_reviews : 0} reviews)
            </span>
          </div>
          <div className="organisation-page__hero-actions">
            <button className="organisation-page__hero-button">
              Book an Appointment
            </button>
            {(() => {
              // Find primary office (head > main > first)
              const primaryOffice = organisation.offices?.find(o => o.office_type === 'head') ||
                                  organisation.offices?.find(o => o.office_type === 'main') ||
                                  organisation.offices?.[0];
              
              return primaryOffice?.phone_number && (
                <a 
                  href={`tel:${primaryOffice.phone_number}`}
                  className="organisation-page__hero-button"
                >
                  Call {primaryOffice.phone_number}
                  {organisation.offices && organisation.offices.length > 1 && (
                    <span className="text-xs opacity-75 block">
                      ({organisation.offices.length} offices)
                    </span>
                  )}
                </a>
              );
            })()}
            <button 
              className="organisation-page__hero-button"
              onClick={() => setSelectedTab(4)} // Switch to Contact tab
            >
              Send an Enquiry
            </button>
            {(() => {
              // Find primary office for website
              const primaryOffice = organisation.offices?.find(o => o.office_type === 'head') ||
                                  organisation.offices?.find(o => o.office_type === 'main') ||
                                  organisation.offices?.[0];
              
              return primaryOffice?.website && (
                <a 
                  href={addUtmParams(primaryOffice.website)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="organisation-page__hero-button"
                >
                  Visit Website
                </a>
              );
            })()}
            <button 
              className="organisation-page__hero-button"
              onClick={() => setSelectedTab(2)} // Switch to Reviews tab
            >
              Leave a Review
            </button>
          </div>
        </div>

        <div className="organisation-page__container">
          <div className="organisation-page__overview">
            <h2 className="organisation-page__overview-title">Overview</h2>
            {/* <p className="organisation-page__overview-content">{organisation.overview}</p> */}
          </div>

          <div className="organisation-page__tabs">
            <Tab.Group onChange={setSelectedTab}>
              <Tab.List className="organisation-page__tabs-list">
                {tabs.map((tab) => (
                  <Tab
                    key={tab.name}
                    className={({ selected }) =>
                      `organisation-page__tabs-item ${
                        selected
                          ? 'organisation-page__tabs-item--selected'
                          : 'organisation-page__tabs-item--default'
                      }`
                    }
                  >
                    {tab.name}
                  </Tab>
                ))}
              </Tab.List>
              <Tab.Panels className="organisation-page__tabs-content">
                {tabs.map((tab, idx) => (
                  <Tab.Panel key={idx} className="organisation-page__tabs-content-panel">
                    {tab.content}
                  </Tab.Panel>
                ))}
              </Tab.Panels>
            </Tab.Group>
          </div>
        </div>
      </div>
    </>
  );
};

export default OrganisationPage; 